import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";

/**
 * /login — Google sign-in via Firebase Auth (architecture §5.3). When the
 * user is already signed in we redirect to wherever they were trying to go
 * (passed as `state.from` by RequireAuth) or to /studies.
 *
 * Note: signed-in ≠ whitelisted. A user with a Google account that isn't in
 * `identity.cms_users` will land on /studies but get 403s from the api. The
 * studies list shows the 403 as "Access denied — your email isn't on the
 * admin whitelist."
 */
export function Login() {
  const { user, loading, signIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Already signed in → bounce out of /login.
  useEffect(() => {
    if (!loading && user) {
      const from =
        (location.state as { from?: { pathname: string } } | null)?.from
          ?.pathname ?? "/studies";
      navigate(from, { replace: true });
    }
  }, [loading, user, location.state, navigate]);

  async function handleSignIn() {
    setBusy(true);
    setError(null);
    try {
      await signIn();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Sign-in failed — try again",
      );
      setBusy(false);
    }
  }

  return (
    <div className="min-h-svh flex items-center justify-center px-4">
      <div className="w-full max-w-[420px] bg-surface rounded-card shadow-card p-8 text-center">
        <p className="font-serif text-title-s text-surface-ink mb-2">
          Lea admin
        </p>
        <p className="text-body-m text-surface-subtle mb-6">
          Sign in with your Leaply Google account.
        </p>
        <button
          type="button"
          onClick={() => void handleSignIn()}
          disabled={busy || loading}
          className="w-full inline-flex items-center justify-center gap-2 rounded-pill bg-surface-ink text-white h-12 px-6 text-body-s font-medium hover:bg-surface-ink/90 disabled:bg-surface-ink/30 disabled:cursor-not-allowed transition-colors"
        >
          {busy ? "Signing in…" : "Continue with Google"}
        </button>
        {error && (
          <p role="alert" className="mt-4 text-body-s text-red-700">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
